// src/Pages/OrderTracking.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

const steps = [
  { title: "Order Placed", desc: "We have received your order." },
  { title: "Preparing", desc: "Your items are being prepared." },
  { title: "Out for Delivery", desc: "Your order is on the way." },
  { title: "Delivered", desc: "Order delivered successfully." },
];

const statusToStep = {
  Pending: 0,
  Preparing: 1,
  "Out for Delivery": 2,
  Delivered: 3,
  Cancelled: 0,
};

const OrderTracking = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  // -------------------------
  // FETCH USER NAME & PHONE
  // -------------------------
  useEffect(() => {
    if (!currentUser) return;

    const fetchUserInfo = async () => {
      let name = currentUser.displayName || "";
      let phone = currentUser.phoneNumber || "";
      let address = "";

      try {
        // Try Firestore user document
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          name = name || data.name || "";
          phone = phone || data.phone || "";
          address = data.address || "";
        }
      } catch (err) {
        console.error("Failed to fetch user doc:", err);
      }

      setUserInfo({ name, phone, address });
    };

    fetchUserInfo();
  }, [currentUser]);

  // -------------------------
  // FETCH ORDERS
  // -------------------------
  useEffect(() => {
    if (!currentUser) return;

    const ordersRef = collection(db, "orders");
    const q = query(ordersRef, where("userId", "==", currentUser.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userOrders = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort(
            (a, b) =>
              (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0)
          );
        setOrders(userOrders);
        setLoading(false);
      },
      (err) => {
        console.error("Orders snapshot error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // -------------------------
  // CANCEL ORDER
  // -------------------------
  const handleCancel = async (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.status === "Cancelled") return;
    const confirmCancel = window.confirm("Are you sure you want to cancel this order?");
    if (!confirmCancel) return;

    try {
      setCancelingOrderId(orderId);
      await updateDoc(doc(db, "orders", orderId), { status: "Cancelled" });
      alert("Order cancelled successfully.");
    } catch (err) {
      console.error(err);
      alert("Failed to cancel order. Try again.");
    } finally {
      setCancelingOrderId(null);
    }
  };

  // -------------------------
  // DELETE ORDER (hard delete)
  // -------------------------
  const handleDelete = async (orderId) => {
    const confirmDelete = window.confirm("Are you sure you want to permanently delete this order?");
    if (!confirmDelete) return;

    try {
      setDeletingOrderId(orderId);
      await deleteDoc(doc(db, "orders", orderId));
      // Optionally you can show a toast
      alert("Order deleted.");
    } catch (err) {
      console.error("Failed to delete order:", err);
      alert("Failed to delete order. Try again.");
    } finally {
      setDeletingOrderId(null);
    }
  };

  if (!currentUser) return <p className="p-5">Please login to see your orders.</p>;
  if (loading || !userInfo) return <p className="p-5">Loading your orders...</p>;
  if (orders.length === 0) return <p className="p-5">You have no orders yet.</p>;

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 md:px-20">
      <h1 className="text-3xl font-bold text-center mb-10">Your Recent Orders</h1>

      <div className="space-y-6">
        {orders.map((order, idx) => (
          <OrderCard
            key={order.id}
            order={order}
            orderNumber={idx + 1}
            userInfo={userInfo}
            isMostRecent={idx === 0}
            canceling={cancelingOrderId === order.id}
            onCancel={() => handleCancel(order.id)}
            deleting={deletingOrderId === order.id}
            onDelete={() => handleDelete(order.id)}
          />
        ))}
      </div>
    </div>
  );
};

// ----------------------------------------
// ORDER CARD
// ----------------------------------------
const OrderCard = ({
  order,
  orderNumber,
  userInfo,
  isMostRecent,
  canceling,
  onCancel,
  deleting,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(isMostRecent);
  const [currentStep, setCurrentStep] = useState(statusToStep[order.status] || 0);
  const [visibleSteps, setVisibleSteps] = useState(new Array(steps.length).fill(false));
  const [visibleInfo, setVisibleInfo] = useState([false, false]);

  useEffect(() => {
    setCurrentStep(statusToStep[order.status] || 0);

    if (!expanded) return;

    setVisibleSteps(new Array(steps.length).fill(false));
    setVisibleInfo([false, false]);

    steps.forEach((_, i) => {
      setTimeout(() => {
        setVisibleSteps((prev) => {
          const x = [...prev];
          x[i] = true;
          return x;
        });
      }, i * 200);
    });

    setTimeout(() => setVisibleInfo([true, false]), steps.length * 200 + 300);
    setTimeout(() => setVisibleInfo([true, true]), steps.length * 200 + 700);
  }, [order.status, order.id, expanded]);

  const toggleExpand = () => setExpanded((s) => !s);

  const stepClass = (i) =>
    expanded ? (visibleSteps[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10") : "opacity-0";

  const infoClass = (idx) =>
    expanded ? (visibleInfo[idx] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10") : "opacity-0";

  const calculateTotal = () =>
    order.cartItems?.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;

  const orderDate = order.createdAt?.toDate?.();
  const formattedDate = orderDate ? orderDate.toLocaleString() : "N/A";

  // ----------------------------------------
  // MERGE GOOGLE/FIRESTORE INFO INTO ORDER
  // ----------------------------------------
  const customerName = order.userName || userInfo.name || "Customer";
  const customerPhone = order.phone || userInfo.phone || "N/A";
  const customerAddress =
    order.address || order.location?.address || userInfo.address || "No address provided";

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-500">
      {/* HEADER (with delete button on right) */}
      <div
        className="flex justify-between items-center p-6 bg-gray-50 hover:bg-gray-100"
        // clicking header toggles expand
        onClick={toggleExpand}
      >
        <div>
          <h2 className="text-xl font-bold">Order No: #{orderNumber}</h2>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right mr-2">
            <span className="text-gray-500 text-sm block">{formattedDate}</span>
            <div className="text-sm font-semibold text-gray-500">{expanded ? "▲" : "▼"}</div>
          </div>

          {/* Delete button (stopPropagation so it doesn't toggle expand) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onDelete) onDelete();
            }}
            disabled={deleting}
            className="ml-2 bg-transparent hover:bg-red-100 text-red-600 border border-red-200 hover:border-red-300 px-3 py-2 rounded flex items-center gap-2 text-sm"
            title="Delete order"
          >
            {deleting ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            ) : (
              // trash icon
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.137 21H7.863a2 2 0 01-1.996-1.858L5 7m5 4v6m4-6v6M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
              </svg>
            )}
            <span className="hidden sm:inline">{deleting ? "Deleting..." : "Delete"}</span>
          </button>
        </div>
      </div>

      {/* Collapsible content */}
      <div
        className={`transition-all duration-500 ${expanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"} overflow-hidden px-6 pt-4 pb-6`}
      >
        <p className="mb-4"><strong>Status:</strong> {order.status || "Pending"}</p>

        {/* Show OTP only when expanded */}
        {expanded && order.status === "Out for Delivery" && order.deliveryOTP && (
          <p className="mb-4 text-green-600">
            OTP for delivery verification: <strong>{order.deliveryOTP}</strong>
          </p>
        )}

        {/* Progress Tracker (same style as your original) */}
        <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 mb-6 relative">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`flex items-center md:items-start flex-col relative transition-all duration-500 ${stepClass(i)}`}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full border-4 transition-all duration-500 ${
                  i < currentStep
                    ? "bg-orange-500 border-orange-500 text-white"
                    : i === currentStep
                    ? "bg-orange-500 border-orange-700 text-white animate-pulse"
                    : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {i + 1}
              </div>

              {i < steps.length - 1 && (
                <div
                  className={`absolute top-full mt-2 h-12 w-1 bg-gray-300 md:top-5 md:left-full md:h-1 md:w-20 md:mt-0 md:ml-2 transition-all duration-500 ${
                    i < currentStep ? "bg-orange-500" : ""
                  }`}
                />
              )}

              <div className="text-center mt-3">
                <h4 className="font-semibold">{step.title}</h4>
                <p className="text-sm text-gray-500">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Delivery Address & Items */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className={`bg-white p-4 rounded-lg shadow transition-all duration-700 ${infoClass(0)}`}>
            <h3 className="font-semibold mb-2">Delivery Address</h3>
            <p><strong>Name:</strong> {customerName}</p>
            <p><strong>Address:</strong> {customerAddress}</p>
            <p><strong>Phone:</strong> {customerPhone}</p>
          </div>

          <div className={`bg-white p-4 rounded-lg shadow transition-all duration-700 ${infoClass(1)}`}>
            <h3 className="font-semibold mb-2">Items Ordered</h3>
            <div className="space-y-2">
              {order.cartItems?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <img
                      src={item.images?.[0] || `https://source.unsplash.com/60x60/?food,${idx}`}
                      className="w-12 h-12 object-cover rounded"
                      alt={item.name}
                    />
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-orange-500">₹{item.price * item.quantity}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t pt-2 text-right">
              <span className="font-bold">Total: ₹{calculateTotal()}</span>
            </div>
          </div>
        </div>

        {/* Cancel Button */}
        {order.status !== "Cancelled" && (
          <div className="text-center mt-4">
            <button
              onClick={onCancel}
              disabled={canceling}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-semibold text-sm disabled:opacity-50"
            >
              {canceling ? "Cancelling..." : "Cancel Order"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
